import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  Toolbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
// import Freetrial from "./Layout/Freetrial";
import CloseIcon from "@mui/icons-material/Close";
import "../../src/styles/Header.scss";
import { useTranslation } from "react-i18next";

const VISUALIZE__URL = import.meta.env.VITE_VISUALIZE__URL;

const logo = VISUALIZE__URL + "clinical_visuals_logo.webp";

const Header = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  // const [openTrialModal, setOpenTrialModal] = useState(false);
  const [scrollOpacity, setScrollOpacity] = useState(0);

  const menuItems = ["home", "about_us", "contact_us"];
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const viewportHeight = window.innerHeight;
      const threshold = viewportHeight * 0.01;
      const newOpacity = Math.min(scrollPosition / threshold, 1);
      setScrollOpacity(newOpacity);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const handleMenuItemClick = (item: string) => {
    if (item === "home") {
      navigate("/");
    } else if (item === "contact_us") {
      navigate("/contactus");
    } else if (item === "about_us") {
      navigate("/aboutus");
    } else if (item === "services") {
      navigate("/services");
    }

    window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll to top on page loads
    setDrawerOpen(false); // Close drawer after navigation
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen); // Toggle the drawer open/close
  };

  return (
    <>
      <AppBar
        position="fixed"
        className="hero "
        sx={{
          backgroundColor: `rgba(255, 255, 255, ${scrollOpacity})`,
          boxShadow: scrollOpacity > 0.1 ? 1 : "none",
          // transition: "background-color 0.3s ease, box-shadow 0.3s ease",
          zIndex: 10,
          minHeight: "48px !important",
          padding: "8px !important",
        }}
      >
        <Toolbar className="main">
          <IconButton
            color="inherit"
            className="toggle"
            sx={{
              display: { sm: "none", md: "none" },
              color: { xs: "#1976d2", sm: "inherit" },
            }}
            onClick={toggleDrawer}
          >
            <MenuIcon />
          </IconButton>
          <img
            className="logo header-fade"
            src={logo}
            alt="Clinical visuals"
            width={200}
            height="auto"
            style={{ width: "200px", height: "auto" }}
            onClick={() => {
              navigate("/");
              window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll to top on page load
            }}
          />

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex", md: "flex" },
              justifyContent: "center",
              fontSize: "18px  ",
              fontWeight: 900,
              gap: { lg: 2 },
              flexGrow: { lg: 20 },
            }}
          >
            {menuItems.map((item) => (
              <Button
                key={item}
                ref={buttonRef}
                sx={{
                  fontSize: { xs: "12px", sm: "12px", md: "18px" },
                  fontWeight: "bold",
                  textTransform: "none",
                  color: scrollOpacity === 1 ? "black" : "transparent",
                }}
                onClick={() => handleMenuItemClick(item)}
              >
                {t(item)} {/* Translated text */}
              </Button>
            ))}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              flexGrow: 0,
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, lg: 3 },
              width: { xs: "auto", sm: "auto", md: "auto" },
            }}
          >
            <Button
              variant="contained"
              onClick={() => navigate("/login")}
              sx={{
                textTransform: "none",
                background: "#1976D2",
                color: "white",
                height: { xs: "35px", md: "50px" },
                fontSize: { xs: "12px", md: "17px" },
                fontWeight: "bold",
                borderRadius: "30px",
                width: { xs: "84px", sm: "150px" },
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  background: "#1565C0",
                  transform: "translateY(-2px)",
                  boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.3)",
                },
                "&:active": {
                  transform: "translateY(1px)",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                },
                border: "2px solid white",
              }}
            >
              {t("login")}
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate("/freetrial")}
              sx={{
                textTransform: "none",
                background: "#00a646",
                color: "white",
                height: { xs: "35px", md: "50px" },
                fontSize: { xs: "12px", md: "17px" },
                borderRadius: "30px",
                fontWeight: "bold",
                width: { xs: "90px", sm: "170px" },
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                animation: "bounce 2s infinite",
                "@keyframes bounce": {
                  "0%, 20%, 50%, 80%, 100%": { transform: "translateY(0)" },
                  "40%": { transform: "translateY(-6px)" },
                  "60%": { transform: "translateY(-3px)" },
                },
                transition: "background 0.2s, box-shadow 0.2s",
                "&:hover": {
                  background: "#00a747",
                  boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.3)",
                  animation: "none",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(1px)",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                },
              }}
            >
              {t("free_trial")}
            </Button>

            {/* Free Trial Modal */}
            {/* <Freetrial
              openTrialModal={openTrialModal}
              setOpenTrialModal={setOpenTrialModal}
            /> */}
          </Box>

          {/* Mobile Menu */}

          <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
            <Box
              sx={{
                width: 200,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff", // Entire background color
                height: "100%", // Full height of the drawer
              }}
            >
              {/* Logo Section with Bottom Shadow */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  padding: 3,
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.4)", // Bottom shadow for logo
                }}
              >
                <IconButton
                  onClick={toggleDrawer}
                  sx={{
                    position: "absolute",
                    right: 3,
                    top: 0,
                    color: "#1976d2",
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <img
                  src={logo}
                  alt="Logo"
                  style={{ width: 120, height: "auto" }}
                />
              </Box>

              {/* Menu Section */}
              <Box
                sx={{
                  paddingY: 2,
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1, // Allows the menu to expand
                }}
              >
                {menuItems.map((item) => (
                  <Button
                    className="menu"
                    key={item}
                    fullWidth
                    onClick={() => handleMenuItemClick(item)}
                    sx={{
                      justifyContent: "left",
                      paddingLeft: "30px",
                      borderBottom: "1px solid #b6b6b629", // White border for menu items
                      color: "#000",
                    }}
                  >
                    {t(item)}
                  </Button>
                ))}

                {/* Login Button with Centered Text */}
                <Button
                  fullWidth
                  onClick={() => navigate("/login")}
                  sx={{
                    textAlign: "center",
                    color: "white",
                    justifyContent: "left",
                    paddingLeft: "30px",
                  }}
                >
                  Login
                </Button>
              </Box>
            </Box>
          </Drawer>
        </Toolbar>
      </AppBar>
    </>
  );
};
export default Header;
